import { CardContent, CardHeader, FormControl, FormControlLabel, FormHelperText, Grid2, IconButton, Stack, Switch } from "@mui/material"
import DashboardLayout from "../../../layoutcontainers/DashboardLayout"
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "../../../context/popup";
import { useLoading } from "../../../components/Loading";
import MaterialTypography from "../../../components/MaterialTypography";
import { ArrowBack } from "@mui/icons-material";
import MaterialSelect from "../../../components/MasterSelect";
import MaterialInput from "../../../components/MaterialInput";
import { useWorkflowDictionaryFormHandling } from "./components/WorkflowDictionaryStateHandle";
import { fetchData, fetchDataById, postData, putData } from "../../../services";
import { useEffect, useState } from "react";
import MaterialButton from "../../../components/MaterialButton";
import MaterialBox from "../../../components/MaterialBox";
import DropDownConfig from "../../Utils/DropDownConfig";

const WorkflowDictionaryForm = () => {

    const [searchParams] = useSearchParams();
    const workflowDictionaryId = searchParams.get("id");
    const pageType = searchParams.get("pageType");
    const pageMode = pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { setCircleLoading } = useLoading();

    const [generalProductOptions, setGeneralProductOptions] = useState([]);
    const [registryOptions, setRegistryOptions] = useState([]);
    const [workflowCategoryOptions, setWorkflowCategoryOptions] = useState([]);

    useEffect(() => {
        if (pageMode === "add" || !workflowDictionaryId) {
            return;
        }
        //Fetch data by Id
        const fetchWorkflowDictionaryById = async (workflowDictionaryId) => {
            try {
            setCircleLoading(true);
            const response = await fetchDataById(`/orchestration/work-flow-dictionary/${workflowDictionaryId}`);

            const responseData = response?.data;
            
            if (response.success) {
                const mapped = {
                    name: responseData?.name || "",
                    code: responseData?.code || "",
                    version: responseData?.version || "",
                    description: responseData?.description || "",
                    effectiveFrom: new Date(responseData?.effectiveFrom).toISOString().split("T")[0] || "" || "",
                    effectiveTo: new Date(responseData?.effectiveTo).toISOString().split("T")[0] || "",

                    // dropdowns (reverse of payload)
                    gProductId: responseData?.product
                    ? { value: responseData.product.id, label: responseData.product.keyName }
                    : null,

                    iRegistryId: responseData?.insurer
                    ? { value: responseData.insurer?.id, label: responseData.insurer?.gInsurerName }
                    : null,

                    workflowCategoryId: responseData?.workflowCategory
                    ? { value: responseData.workflowCategory.id, label: responseData.workflowCategory.keyName }
                    : null,

                    isActive: responseData?.isActive,

                    // status fields
                    codeStatus: responseData?.code ? "success" : "unchecked",
                    nameStatus: responseData?.name ? "success" : "unchecked",
                    versionStatus: responseData?.version ? "success" : "unchecked",
                    descriptionStatus: responseData?.description ? "success" : "unchecked",
                    gProductIdStatus: responseData?.product ? "success" : "unchecked",
                    iRegistryIdStatus: responseData?.iRegistryId ? "success" : "unchecked",
                    workflowCategoryIdStatus: responseData?.workflowCategory ? "success" : "unchecked",
                    effectiveFromStatus: responseData?.effectiveFrom ? "success" : "unchecked",
                    effectiveToStatus: responseData?.effectiveTo ? "success" : "unchecked"
                };

                setFormData(mapped);  
            }
            } catch (error) {
            console.error("Error fetching data by ID:", error);
            showSnackbar(error?.response?.data?.message || "Network Error","error");
            }finally{
                setCircleLoading(false);
            }
        };
        fetchWorkflowDictionaryById(workflowDictionaryId);
    }, [pageMode]);

    useEffect(() => {
        if(pageMode === "view"){
            return
        }
        fetchAllDropdowns();
    }, [pageMode]);

    //all dropdown api function
    const fetchAllDropdowns = async () => {
        setCircleLoading(true);

        try {
            const [
            productResponse,
            iRegistryResponse,
            workflowCategoryResponse
            ] = await Promise.allSettled([
            fetchData(`/reference/general/key-value-store?code=${DropDownConfig?.generalProduct}&dropdown=true&isActive=true`),
            fetchData(`/reference/api-engine/insurer-registry?dropdown=true`),
            fetchData(`/orchestration/key-value-store?code=${DropDownConfig?.operationWorkflowCategory}&dropdown=true`)
            ]);
            
            const formattedProducts = productResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.keyName,
            })) || [];

            const formattedInsurerRegistries = iRegistryResponse?.value?.data?.insurer_registries?.map((item) => ({
                value: item?.id,
                label: item?.gInsurerName,
            })) || [];

            const formattedWorkflowCategories = workflowCategoryResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.keyName,
            })) || [];
            
            setGeneralProductOptions(formattedProducts);
            setRegistryOptions(formattedInsurerRegistries);
            setWorkflowCategoryOptions(formattedWorkflowCategories);

        } catch (error) {
            console.error("Error fetching dropdown data:", error);
            showSnackbar("Error Fetching Dropdown","warning");
        } finally {
            setCircleLoading(false);
        }
    };

    //post and put api function
    const handleSubmitWorkflowDictionary = async () => {
        const payload = {
            code: formData?.code,
            name: formData?.name,
            gProductId: formData?.gProductId?.value,
            iRegistryId: formData?.iRegistryId?.value,
            workflowCategoryId: formData?.workflowCategoryId?.value,
            description: formData?.description,
            version: formData?.version,
            effectiveFrom: formData?.effectiveFrom,
            effectiveTo: formData?.effectiveTo,
            isActive: formData?.isActive
        };
        //Update
        if(workflowDictionaryId){
            try {
                const response = await putData(`/orchestration/work-flow-dictionary/${workflowDictionaryId}`, payload);
                
                const responseMessage = response?.message;
                if(response?.success){
                //success message
                    showSnackbar(responseMessage, "success");
                    navigate('/operation/workflow-dictionary');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage, "error")
                }
            } catch (error) {
                console.error('Error updating data:', error);
                showSnackbar(error?.response?.data?.message || "Network Error","error");
            }
        }
        else{      
        //Submit
            try {
                const response = await postData('/orchestration/work-flow-dictionary', payload);                
                const responseMessage = response?.message;
                if(response?.success){
                    //success message
                    showSnackbar(responseMessage, "success");
                    navigate('/operation/workflow-dictionary');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage, "error");
                }
            } catch (error) {
                //catch error message
                console.error('Error posting data:', error);
                showSnackbar(error?.response?.data?.message || "Network Error","error")
            }
        }
    };

    const { formData, setFormData, handleChange, checkAllFields } = useWorkflowDictionaryFormHandling(); 

  return (
    <DashboardLayout>
        <CardHeader
        title={
            <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={() => navigate("/operation/workflow-dictionary")}>
                        <ArrowBack />
                    </IconButton>
                    <Stack direction="column" spacing={0}>
                    <MaterialTypography variant="h5" fontWeight="medium">
                        Workflow Dictionary Details
                    </MaterialTypography>
                    <MaterialTypography variant="caption" color="text">
                        Enter New Workflow Dictionary Details.
                    </MaterialTypography>
                    </Stack>
                </Stack>
            </Stack>
        }
        />
        <CardContent>
            <Grid2 container spacing={1}>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography component="label" variant="caption" fontWeight="bold">
                        Name*
                    </MaterialTypography>
                    <MaterialInput
                        name="name"
                        placeholder="eg. Field Name"
                        value={formData.name}
                        onChange={handleChange}
                        success={formData.nameStatus === "success"}
                        error={formData.nameStatus === "error"}
                        disabled={pageMode === "view"}
                    />
                    {formData.nameStatus === "error" && (
                        <FormControl error>
                        <FormHelperText>
                            Name should be a minimum 1 character & maximum 100 characters long.
                        </FormHelperText>
                        </FormControl>
                    )}
                </Grid2>
                 {/* gProductId */}
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography component="label" variant="caption" fontWeight="bold">
                    General Product*
                    </MaterialTypography>
                    <MaterialSelect
                    name="gProductId"
                    value={formData.gProductId}
                    onChange={handleChange}
                    options={generalProductOptions}
                    success={formData.gProductIdStatus === "success"}
                    error={formData.gProductIdStatus === "error"}
                    isDisabled={pageMode === "view"}
                    />
                </Grid2> 
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold">
                        Code
                    </MaterialTypography>
                    <MaterialInput
                    name="code"
                    placeholder="eg. WORFLD_001"
                    value={formData.code}
                    onChange={handleChange}
                    success={formData.codeStatus === "success"}
                    error={formData.codeStatus === "error"}
                    disabled={pageMode === "view"}
                    />
                    {formData.codeStatus === "error" && (
                        <FormControl error>
                            <FormHelperText>
                            Code should be a minimum 1 character & maximum 100
                            characters long.
                            </FormHelperText>
                        </FormControl>
                    )}
                </Grid2>
                {/* iRegistryId */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <MaterialTypography variant="caption" fontWeight="bold">
                        Insurer Registry
                    </MaterialTypography>
                    <MaterialSelect
                        name="iRegistryId"
                        value={formData.iRegistryId}
                        options={registryOptions}
                        onChange={handleChange}
                        success={formData.iRegistryIdStatus === "success"}
                        error={formData.iRegistryIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                <MaterialTypography variant="caption" fontWeight="bold">
                    Workflow Category
                </MaterialTypography>
                <MaterialSelect
                    name="workflowCategoryId"
                    value={formData.workflowCategoryId}
                    options={workflowCategoryOptions}
                    onChange={handleChange}
                    success={formData.workflowCategoryIdStatus === "success"}
                    error={formData.workflowCategoryIdStatus === "error"}
                    isDisabled={pageMode === "view"}
                />
                </Grid2>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold">
                        Version
                    </MaterialTypography>
                    <MaterialInput
                    name="version"
                    placeholder="eg. 1.1"
                    value={formData.version}
                    onChange={handleChange}
                    success={formData.versionStatus === "success"}
                    error={formData.versionStatus === "error"}
                    disabled={pageMode === "view"}
                    />
                    {formData.versionStatus === "error" && (
                        <FormControl error>
                            <FormHelperText>
                            Version Status should be a minimum 1 character & maximum 10
                            characters long.
                            </FormHelperText>
                        </FormControl>
                    )}
                </Grid2>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold">
                        Effective From*
                    </MaterialTypography>
                    <MaterialInput
                    name="effectiveFrom"
                    value={formData.effectiveFrom}
                    onChange={handleChange}
                    success={formData.effectiveFromStatus === "success"}
                    error={formData.effectiveFromStatus === "error"}
                    disabled={pageMode === "view"}
                    type="date"
                    />
                </Grid2>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold">
                        Effective Till*
                    </MaterialTypography>
                    <MaterialInput
                    name="effectiveTo"
                    value={formData.effectiveTo}
                    onChange={handleChange}
                    success={formData.effectiveToStatus === "success"}
                    error={formData.effectiveToStatus === "error"}
                    disabled={pageMode === "view" || formData.effectiveFrom === ""}
                    type="date"
                    inputProps={{ min: formData.effectiveFrom }}
                    />
                </Grid2>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold">
                        Description
                    </MaterialTypography>
                    <MaterialInput
                    name="description"
                    placeholder="eg. Workflow Description"
                    value={formData.description}
                    onChange={handleChange}
                    success={formData.descriptionStatus === "success"}
                    error={formData.descriptionStatus === "error"}
                    disabled={pageMode === "view"}
                    multiline
                    rows={3}
                    />
                    {formData.descriptionStatus === "error" && (
                        <FormControl error>
                            <FormHelperText>
                            Description Status should be a minimum 1 character & maximum 100
                            characters long.
                            </FormHelperText>
                        </FormControl>
                    )}
                </Grid2>
                <Grid2 size={{lg:2, md:2, sm: 12, xs: 12 }}>
                        <MaterialTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold">
                            Active
                        </MaterialTypography>
                    <FormControlLabel
                    control={
                        <Switch
                            checked={formData.isActive}
                            disabled={pageMode === "view"}
                            onChange={(e) =>
                                handleChange({
                                    target: {
                                        name: "isActive",
                                        value: e.target.checked,
                                    },
                                })
                            }
                            color="primary"
                        />
                    }
                    label={
                        <MaterialTypography variant="caption" fontWeight="bold">
                            {formData?.isActive ? "Yes" : "No"}
                        </MaterialTypography>
                    }
                    sx={{ marginLeft: 0 }}
                    />
                </Grid2>
            </Grid2>
        </CardContent>
        <MaterialBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        >
            {pageMode !== "view" && (
            <>
                <MaterialButton
                sx={{ m: 2 }}
                variant="outlined"
                color="dark"
                onClick={() => navigate("/operation/workflow-dictionary")}
                >
                Cancel
                </MaterialButton>
                <MaterialButton
                sx={{ m: 2 }}
                variant="gradient"
                color="dark"
                disabled={!checkAllFields()}
                onClick={handleSubmitWorkflowDictionary}
                >
                {pageMode === "edit" ? "Update" : "Submit"}
                </MaterialButton>
            </>
            )}
        </MaterialBox>
    </DashboardLayout>
  )
}

export default WorkflowDictionaryForm